<template>
  <div class="chat-submit">
    <template slot="placeholder">
      <Loader :loading="true" theme="block" />
    </template>

    <form v-if="!selectedMode" class="chat-submit__form" @submit.prevent="handleSubmit">
      <div class="editor">
        <input :id="_uid" ref="uploadInput" type="file" @change="handleUpload" />

        <div v-if="editable.id" class="editor__reply">
          <div class="editor__reply-delete" @click="handleReplyDelete">
            <SvgIcon name="close" />
          </div>
          <span class="editor__reply-title">{{ editable.content }}</span>
        </div>

        <div class="editor__body">
          <ChatInput
            class="editor__textarea"
            :placeholder="$t('chat.submit.message-placeholder')"
            v-model="text"
            ref="chatInput"
            @keyup.ctrl.native="handleSubmit"
          />

          <div class="editor__actions">
            <div class="editor__emoji" :class="[emodjiOpened && 'is-active']" v-click-outside="closeEmodji">
              <span class="editor__emoji-trigger" @click.prevent="emodjiOpened = !emodjiOpened">
                <SvgIcon name="smile" />
              </span>
              <div class="editor__emoji-plugin">
                <VEmojiPicker @select="selectEmoji" :emojisByRow="5" :emojiSize="32" :showSearch="false" />
              </div>
            </div>
            <label :for="_uid" class="editor__attach">
              <SvgIcon name="paper-clip" />
            </label>
          </div>

          <button class="primary editor__submit">
            <SvgIcon name="chat-submit" />
          </button>
        </div>
      </div>
    </form>
    <div class="chat-submit__select-mode" v-else>
      <div class="chat-submit__select-mode-count">{{ count }} message selected</div>
      <div class="chat-submit__line" @click="$emit('forwardSelected')">
        <svg width="17" height="16" viewBox="0 0 17 16" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M10.0826 3.3915V5.44866C10.0826 5.53198 10.015 5.59953 9.93172 5.59953C6.69457 5.59953 3.81833 7.66499 2.78396 10.7324L1.68211 14L3.23004 12.4521C5.00367 10.6784 7.3573 9.60371 9.85922 9.425C9.97992 9.41638 10.0826 9.51197 10.0826 9.63298V11.6634C10.0826 12.0151 10.5108 12.1878 10.7548 11.9345L14.8907 7.63942C15.0405 7.4839 15.0357 7.2364 14.8801 7.08675L10.7441 3.10985C10.4959 2.87114 10.0826 3.04709 10.0826 3.3915Z"
            stroke="#CCD7E6" stroke-width="2" stroke-linejoin="round" />
        </svg>
        <div>Forward</div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex'
import { bytesToMegaBytes } from '@/utils/bytes'
import { VEmojiPicker } from 'v-emoji-picker'

import { withUi } from '@/components/ui'
import outsize from '@/mixins/outsize'
import handleErrorWithToast from '@/utils/handleErrorWithToast'
import ChatInfo from '@/components/chat/ChatInfo.vue'
import ChatInput from '@/components/chat/ChatInput.vue'
import socketDialog from '@/mixins/socketDialog'

export default {
  props: {
    selectedMode: Boolean,
    count: Number,
    replyMessage: Object,
  },
  components: { ChatInfo, VEmojiPicker, ChatInput },
  mixins: [outsize, withUi, socketDialog],
  data() {
    return {
      typingTimeout: null,
      typing: false,
      text: '',
      uploader: {
        allowedMime: ['image'],
        maxSize: '5',
      },
      emodjiOpened: false,
    }
  },
  computed: {
    ...mapGetters('chat', ['activeDialog', 'editable']),
  },
  methods: {
    onSuccessAdd() {
      this.text = ''
      this.$refs.chatInput.clean()
      this.setEditable({ id: null, content: null })
      this.$emit('onSubmit')
    },
    async handleSubmit() {
      if (this.text.trim().length >= 1) {
        const request = { content: this.text, type: 1, group_id: this.activeDialog, replyId: this.replyMessage?.id }

        if (!this.editable.id) {
          await this.createMessage(request).catch(handleErrorWithToast.bind(this))
        } else {
          await this.editMessage({
            ...request,
            content: `${request.content} (${this.$t('chat.submit.edited')})`,
            id: this.editable.id,
          }).catch(handleErrorWithToast.bind(this))
        }

        this.onSuccessAdd()
      }
    },
    async handleUpload(e) {
      const files = e.target.files

      if (files && files[0]) {
        const file = files[0]

        // limit size
        if (this.uploader.maxSize) {
          const sizeInMb = bytesToMegaBytes(file.size)

          if (sizeInMb > this.maxSize) {
            this.$toast({
              type: 'error',
              title: this.$t('chat.submit.error.title'),
              text: this.$t('chat.submit.error.text', { size: this.maxSize }),
            })
            e.target.value = ''
            return false
          }
        }

        if (file) {
          // await this.createMessageGhost({ file })

          await this.uploadFile({ upload: file, type: 2, content: this.text, group_id: this.activeDialog }).catch(
            handleErrorWithToast.bind(this),
          )

          this.onSuccessAdd()
        }

        // if (this.includeReader) {
        //   const reader = new FileReader();
        //   reader.onload = (ev) => {
        //     this.setFileReaderValue(ev.target.result);
        //   };
        //   reader.readAsDataURL(file);
        // }

        e.target.value = ''
      }
    },

    selectEmoji(emoji) {
      this.$refs.chatInput.addText(emoji.data)
      this.text += emoji.data
      this.closeEmodji()
    },
    closeEmodji() {
      this.emodjiOpened = false
    },

    handleReplyDelete() {
      this.setEditable({
        id: null,
        text: null,
      })
    },
    sendTypingStatus(typing) {
      this.channel.whisper('typing', {
        user: this.$auth.user().id,
        typing,
      })
    },
    ...mapActions('chat', ['createMessage', 'editMessage', 'uploadFile', 'createMessageGhost']),
    ...mapMutations('chat', ['setEditable']),
  },
  watch: {
    text() {
      if (this.typing) {
        clearTimeout(this.typingTimeout)
      } else {
        this.typing = true
        this.sendTypingStatus(true)
      }

      this.typingTimeout = setTimeout(() => {
        this.typing = false
        this.sendTypingStatus(false)
      }, 3000)
    },
    activeDialog() {
      this.$refs.chatInput.clean()
      this.text = ''
    },
    editable: {
      deep: true,
      handler: function(v) {
        if (v && v.id) {
          this.text = v.content
        }
      },
    },
  },
}
</script>

<style lang="scss" scoped>
.chat-submit {
  background: #ffffff;
  border-top: 1px solid rgba(180, 187, 198, 0.13);

  &__form {
    padding-right: 26px;
    padding-top: 15px;
    padding-bottom: 14px;

    input {
      position: absolute;
      left: 0;
      top: 0;
      width: 0.1px;
      height: 0.1px;
      opacity: 0;
      visibility: hidden;
    }
  }

  &__line {
    display: flex;
    align-items: center;

    font-size: 15px;
    font-weight: 400;
    line-height: 23.63px;
    cursor: pointer;

    > * + * {
      margin-left: 8px;
    }
  }

  &__select-mode {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    padding: 20px 24px;

    &-count {
      position: absolute;
      left: 50%;
      top: 50%;
      transform: translate(-50%, -50%);

      font-size: 12px;
      font-weight: 700;
      line-height: 17.7px;
      color: #CCD7E6;
    }
  }
}

.editor {
  padding-left: 10px;

  &__reply {
    display: inline-flex;
    align-items: center;
    max-width: 100%;
    min-width: 1px;
    padding: 8px 10px 12px;
  }

  &__reply-title {
    display: inline-block;
    position: relative;
    font-size: 15px;
    padding: 2px 10px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    &::before {
      display: inline-block;
      content: ' ';
      position: absolute;
      top: 0;
      left: 0;
      width: 2px;
      height: 26px;
      background: $primary;
      border-radius: 1px;
    }
  }

  &__reply-delete {
    display: inline-flex;
    justify-content: center;
    align-items: center;
    text-align: center;
    min-width: 20px;
    min-height: 20px;
    margin-right: 12px;
    padding: 4px;
    font-size: 0;
    border: 1px solid $dark;
    border-radius: 50%;
    color: $dark;
    cursor: pointer;
    transition: color 0.25s ease, border 0.25s ease;

    .svg-icon {
      font-size: 8.57px;
    }

    &:hover {
      color: $red;
      border-color: $red;
    }
  }

  &__emoji-trigger {
    display: inline-block;
    color: $dark;
    font-size: 0;
    text-align: center;
    padding: 7px;
    margin: 0;
    cursor: pointer;
    transition: color 0.25s ease;

    .svg-icon {
      font-size: 18px;
    }

    &:hover {
      color: $text;
    }
  }

  &__emoji-plugin {
    position: absolute;
    bottom: calc(100% + 18px);
    right: -80px;
    width: 310px;
    z-index: 2;
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.25s ease;

    .emoji-picker {
      width: 310px;
      background: $bg;
    }

    ::v-deep {
      #Categories {
        background: $bg;
      }

      .container-emoji {
        height: 190px;
      }
    }
  }

  &__emoji {
    &.is-active {
      .editor__emoji-trigger {
        color: $primary;
      }

      .editor__emoji-plugin {
        opacity: 1;
        pointer-events: all;
      }
    }
  }

  &__body {
    position: relative;
    display: flex;
    align-items: center;
    flex: 1 1 auto;
  }

  &__textarea {
    -webkit-appearance: none;
    height: auto;
    resize: none;
    border: 0;
    border-radius: 0;
  }

  &__actions {
    display: flex;

    > * {
      flex: 0 0 auto;
      position: relative;
    }
  }

  &__attach {
    color: $dark;
    font-size: 0;
    text-align: center;
    padding: 7px;
    margin: 0;
    cursor: pointer;
    transition: color 0.25s ease;

    .svg-icon {
      font-size: 18px;
    }

    &:hover {
      color: $text;
    }
  }

  &__submit {
    flex: 0 0 35px;
    width: 35px;
    height: 35px;
    padding: 0;
    margin-left: 5px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0;
    border-radius: 10px;
    color: white;
    cursor: pointer;
    transition: background 0.25s ease-in-out;

    .svg-icon {
      font-size: 16px;
      margin: 0;
    }
  }
}
</style>
